import { Box, ChakraProvider, HStack, Link, Text, useBoolean, VStack } from '@chakra-ui/react';
import libraryTheme from 'library/theme';
import { THEME_COLORS } from 'library/theme/colors';
import { CrownIcon } from 'icons';
import { BillingService, requestUserOrganization } from 'core';
import { Switch, useToast } from 'library/atoms';
import { getFeatureSettings, updateFeatureSettings } from './commands';
import { useEffect, useState, useMemo } from 'react';
import { Loader } from 'library/molecules';
import { connect, useSelector } from 'react-redux';
import { IAppState } from 'core/interfaces/IAppState';
import UpgradeOnlyModal from 'components/upgradeonly.tooltip';
import Tippy from '@tippy.js/react';
import { AppTracker } from 'shared/analytics/tracker';
import { T_WA_UP_IRR_DISABLED, T_WA_UP_IRR_ENABLED } from 'core/const/tracker';

const RESOLUTION_REASON_PLAN = 'incident-resolution-reason';

const OrganizationFeatureSettingsTab = (props: any) => {
  const toast = useToast();
  const [isMandatory, setIsMandatory] = useState(false);
  const [isLoading, setIsLoading] = useBoolean(true);

  useEffect(() => {
    getFeatureSettings()
      .then(val => setIsMandatory(val))
      .finally(() => setIsLoading.off());
  }, []);

  const { resolutionReason, organizationProps } = useSelector((state: IAppState) => ({
    resolutionReason: state.organization.plan.p?.rules[RESOLUTION_REASON_PLAN],
    organizationProps: { organization: state.organization },
  }));

  const { isEnabled, planLimit, isPlanRenewable } = useMemo(() => {
    if (!resolutionReason) {
      return {
        isEnabled: false,
        planLimit: undefined,
        isPlanRenewable: false,
      };
    }

    return {
      isEnabled: true,
      planLimit: undefined,
      isPlanRenewable: resolutionReason.renewable,
    };
  }, [resolutionReason]);

  const onToggle = () => {
    if (isEnabled) {
      const isFeatMandatory = !isMandatory;
      setIsMandatory(isFeatMandatory);

      updateFeatureSettings(isFeatMandatory)
        .then(() => {
          props.requestUserOrganization();
          AppTracker.track(isFeatMandatory ? T_WA_UP_IRR_ENABLED : T_WA_UP_IRR_DISABLED);
        })
        .catch(err => {
          toast({
            status: 'error',
            text: `Error: ${err}`,
          });
          // revert UI optimistic change on API failure
          setIsMandatory(!isFeatMandatory);
        });
    }
  };

  return (
    <ChakraProvider theme={libraryTheme}>
      <Box className="org_settings__container_padding global--soft-scroll">
        <Tippy
          content={
            !isEnabled ? (
              <UpgradeOnlyModal
                hasBillingPermission={BillingService.hasManageBillingPermission(organizationProps)}
                align="left"
                showModal={true}
                message={BillingService.getMessage(
                  planLimit,
                  RESOLUTION_REASON_PLAN,
                  organizationProps,
                  isPlanRenewable,
                )}
                header={BillingService.getHeader(
                  planLimit,
                  RESOLUTION_REASON_PLAN,
                  organizationProps,
                )}
              />
            ) : (
              ''
            )
          }
          interactive={true}
          animation={'scale'}
          placement="top-end"
        >
          <VStack rowGap={'10px'} alignItems="flex-start">
            <Text variant="h3">Feature Settings (1)</Text>
            <Text variant="body" color={THEME_COLORS.brand.lightGray}>
              You can enable or disable set of features within the product
            </Text>

            {isLoading ? (
              <Box width="full" height="full" bg="container.background">
                <Loader />
              </Box>
            ) : (
              <Box width="full" borderWidth="0.5px" padding={3} borderRadius={4} gap={'7px'}>
                <HStack columnGap={'6px'} mb={1}>
                  <Text variant="formInput" color="gray.700">
                    Resolution Reason
                  </Text>

                  <Switch
                    size="md"
                    isOn={isMandatory}
                    handleChange={onToggle}
                    isDisabled={!isEnabled}
                  />

                  {!isEnabled && <CrownIcon width={18} height={18} />}
                </HStack>
                <Text variant="hint" color={THEME_COLORS.brand.lightGray}>
                  Enabling the toggle will make Resolution Reason for every incident mandatory.{' '}
                  <Link
                    color={THEME_COLORS.brand.blue}
                    href="https://support.squadcast.com/incidents-page/incidents-details#understanding-resolution-reason"
                    target="_blank"
                  >
                    Learn More.
                  </Link>
                </Text>
              </Box>
            )}
          </VStack>
        </Tippy>
      </Box>
    </ChakraProvider>
  );
};

export default connect(
  (state: IAppState) => ({
    userOrganizations: state.userOrganizations,
  }),
  {
    requestUserOrganization,
  },
)(OrganizationFeatureSettingsTab);
